import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { DashboardHeader } from '../Dashboard/DashboardUi';
import { Modal } from '../ui/Modal';
import { Field, Input } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import { useAuth } from '../../context/AuthContext';
import { formatDateFr } from '../../lib/formatDate';
import { messageErreur, organisationApi, type DroitsUsage, type LigneUsage, type MetriqueUsage } from '../../lib/organisationApi';
import { EtatEchec, Pastille, Squelettes } from './EtatsPage';

/** Droits d'usage (maquette L01-09) : modules activés, limites et consommation du mois ; alerte à 80 %. */

const MOIS = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });
const fmtNombre = (n: number) => n.toLocaleString('fr-FR');
const fmtDate = (iso: string | null) => formatDateFr(iso) || '—';

const BarreUsage: React.FC<{ m: MetriqueUsage }> = ({ m }) => {
    if (m.limite === null) return <div className="text-[12.5px] text-content-muted">{m.libelle} · {fmtNombre(m.consommation)} ce mois · aucune limite</div>;
    const pct = Math.min(100, m.pourcent ?? 0);
    const alerte = m.niveau === 'seuil' || m.niveau === 'atteinte';
    return (
        <div className="min-w-[200px]">
            <div className="flex flex-wrap items-center justify-between gap-2 text-[12.5px]">
                <span className="text-content"><b className="tabular-nums">{fmtNombre(m.consommation)}</b> sur <span className="tabular-nums">{fmtNombre(m.limite)}</span> {m.libelle}</span>
                {alerte && <Pastille ton="warn" icone={<AlertTriangle size={11} />}>{m.niveau === 'atteinte' ? 'Limite atteinte' : 'Seuil de 80 % dépassé'}</Pastille>}
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-surface-soft overflow-hidden border border-border-soft" role="progressbar" aria-valuenow={m.pourcent ?? 0} aria-valuemin={0} aria-valuemax={100} aria-label={`${m.libelle} : ${m.pourcent ?? 0} % de la limite`}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: alerte ? 'var(--st-warn-fg)' : 'var(--acc)' }} />
            </div>
        </div>
    );
};

const ModaleLimite: React.FC<{ cible: { ligne: LigneUsage; metrique: MetriqueUsage } | null; clientId: string; onClose: () => void; onFait: () => void }> = ({ cible, clientId, onClose, onFait }) => {
    const [valeur, setValeur] = useState('');
    const [erreur, setErreur] = useState('');
    useEffect(() => { setValeur(cible?.metrique.limite != null ? String(cible.metrique.limite) : ''); setErreur(''); }, [cible]);
    const envoyer = async (limite: number | null) => {
        if (!cible) return;
        try {
            await organisationApi.fixerLimite(clientId, { service_id: cible.ligne.service_id, module_name: cible.ligne.module_name, metrique: cible.metrique.metrique, limite });
            onFait(); onClose();
        } catch (e) { setErreur(messageErreur(e)); }
    };
    const n = Number(valeur);
    const ok = valeur !== '' && Number.isInteger(n) && n >= 0;
    return (
        <Modal open={!!cible} onClose={onClose} size="sm" title="Limite d'usage" subtitle={cible ? `${cible.ligne.label} · ${cible.metrique.libelle}` : undefined}
            footer={<>
                {cible?.metrique.limite != null && <button type="button" className={pillSecondary(36)} onClick={() => void envoyer(null)}>Retirer la limite</button>}
                <button type="button" className={pillPrimary(36)} disabled={!ok} onClick={() => void envoyer(n)}>Enregistrer la limite</button>
            </>}>
            <Field label="Limite par mois" error={erreur} help="Sans limite, la consommation n'est pas plafonnée.">{(f) => <Input {...f} inputMode="numeric" value={valeur} onChange={(e) => setValeur(e.target.value.replace(/\D/g, ''))} autoFocus />}</Field>
        </Modal>
    );
};

/**
 * `seulementLimites` : forme intégrée à « Mes modules » (client) — rien ne s'affiche tant qu'aucun module n'a de limite,
 * et seuls les modules qui en ont une sont listés.
 */
export const DroitsUsagePage: React.FC<{ clientId?: string; sansEntete?: boolean; seulementLimites?: boolean }> = ({ clientId, sansEntete, seulementLimites }) => {
    const { user } = useAuth();
    const [donnees, setDonnees] = useState<DroitsUsage | null>(null);
    const [erreur, setErreur] = useState<unknown>(null);
    const [edition, setEdition] = useState<{ ligne: LigneUsage; metrique: MetriqueUsage } | null>(null);
    const charger = useCallback(async () => {
        setErreur(null);
        try { setDonnees(await organisationApi.droitsUsage(clientId)); } catch (e) { setErreur(e); }
    }, [clientId]);
    useEffect(() => { void charger(); }, [charger]);

    const mois = donnees ? MOIS.format(new Date(`${donnees.mois}-01T12:00:00`)) : '';
    const lignes = donnees ? (seulementLimites ? donnees.lignes.filter((l) => l.metriques.some((m) => m.limite !== null)) : donnees.lignes) : [];
    if (seulementLimites && !erreur && (!donnees || (lignes.length === 0 && !donnees.alerte))) return null;
    return (
        <div className="space-y-4 max-w-[1100px]">
            {!sansEntete && <DashboardHeader societe={user?.type === 'admin' ? '' : user?.company_name || ''} titre="Droits d'usage" sousTitre="Modules activés, limites et consommation du mois." />}
            {sansEntete && (
                <div>
                    <h2 className="text-[15px] font-semibold text-content">{seulementLimites ? 'Votre consommation' : 'Limites d\'usage'} <span className="text-content-muted font-normal">· {mois}</span></h2>
                    {seulementLimites && <p className="text-[12.5px] text-content-muted">Certains modules ont un plafond mensuel (par exemple un nombre d'e-mails). Vous êtes prévenu à 80 %.</p>}
                </div>
            )}
            {erreur ? <EtatEchec erreur={erreur} titre="Impossible d'afficher vos droits d'usage" onRetry={charger} /> : !donnees ? <Squelettes lignes={3} /> : (
                <>
                    {donnees.alerte && (
                        <div role="alert" className="flex items-start gap-2.5 rounded-xl bg-state-warn-soft text-state-warn px-4 py-3 text-[13px]">
                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                            <p><b>{donnees.alerte.pourcent} % de vos {donnees.alerte.libelle} du mois sont utilisés</b> ({fmtNombre(donnees.alerte.consommation)} sur {fmtNombre(donnees.alerte.limite || 0)}). {user?.type === 'admin' ? '' : "Contactez L'IAmani pour relever la limite."}</p>
                        </div>
                    )}
                    {lignes.length === 0 ? (
                        <div className="bg-surface border border-border rounded-xl px-5 py-8 text-center">
                            <div className="text-[14px] font-semibold text-content">Aucun module activé</div>
                            <p className="text-[12.5px] text-content-muted mt-1">Les modules que vous activez dans « Mes modules » apparaissent ici, avec leurs limites éventuelles.</p>
                        </div>
                    ) : (
                        <div className="bg-surface border border-border rounded-xl overflow-hidden">
                            <div className="hidden md:grid grid-cols-[minmax(0,1.2fr)_160px_minmax(0,1.6fr)] gap-3 px-4 h-10 items-center border-b border-border bg-surface-soft text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">
                                <span>Module</span><span>Activé le</span><span>Limite et consommation</span>
                            </div>
                            {lignes.map((l) => (
                                <div key={`${l.service_id}-${l.module_name}`} className="grid grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_160px_minmax(0,1.6fr)] gap-x-3 gap-y-1.5 px-4 py-3 border-b border-border-soft last:border-b-0 items-center">
                                    <div className="min-w-0"><span className="block text-[13px] font-semibold text-content truncate">{l.label}</span><span className="block text-[11.5px] text-content-muted truncate">{l.service_name}</span></div>
                                    <div className="text-[12.5px] text-content-muted">{fmtDate(l.activated_at)}</div>
                                    <div className="space-y-2">
                                        {l.metriques.length === 0 ? <span className="text-[12.5px] text-content-muted">Aucune limite</span> : l.metriques.map((m) => (
                                            <div key={m.metrique} className="flex flex-wrap items-center gap-3 justify-between">
                                                <BarreUsage m={m} />
                                                {user?.type === 'admin' && clientId && <button type="button" className={pillSecondary(32)} onClick={() => setEdition({ ligne: l, metrique: m })}>{m.limite === null ? 'Définir une limite' : 'Modifier la limite'}</button>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="text-[12px] text-content-muted px-1">{seulementLimites ? "Les plafonds sont réglés par L'IAmani : contactez-nous pour les relever." : "Coût des modules : tarif défini à la fin. Les limites sont réglées par L'IAmani."}</p>
                </>
            )}
            {clientId && <ModaleLimite cible={edition} clientId={clientId} onClose={() => setEdition(null)} onFait={charger} />}
        </div>
    );
};

export default DroitsUsagePage;
